import { Button } from '@plato/ui-legacy/components/ui/button';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { ChevronDownIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { InsightCard } from '../../insights-common/components/insight-card.tsx';
import type { SendAnalyticsEventHandler } from '../../insights-common/hooks/use-insights-with-analytics.ts';

const INITIAL_VISIBLE_COUNT = 3;

type InsightSectionProps = {
    title: string;
    subtitle?: string;
    insights: (InsightListItem & { key: string })[];
    customerId: string;
    section: 'risks' | 'opportunities';
    sendAnalytics: SendAnalyticsEventHandler;
    setInsightVisibility: (insightId: string, isVisible: boolean) => void;
};

export function InsightSection({
    title,
    subtitle,
    insights,
    customerId,
    section,
    sendAnalytics,
    setInsightVisibility,
}: InsightSectionProps) {
    const { t } = useTranslation();
    const sendEvent = useAnalytics();
    const [isExpanded, setIsExpanded] = useState(false);
    const headingId = useId();

    if (insights.length === 0) {
        return null;
    }

    const visibleInsights = isExpanded ? insights : insights.slice(0, INITIAL_VISIBLE_COUNT);
    const hiddenCount = insights.length - INITIAL_VISIBLE_COUNT;

    return (
        <section aria-labelledby={headingId} className="flex flex-col gap-3 rounded-lg bg-white p-3">
            <div className="flex items-baseline gap-1 bg-white z-10 sticky top-0 -my-3 py-3">
                <Text id={headingId} size="sm" weight="semibold">
                    {title}
                </Text>
                {subtitle && (
                    <Text
                        size="sm"
                        className="leading-4 text-neutral-500 [font-variant-numeric:lining-nums_tabular-nums]"
                    >
                        {subtitle}
                    </Text>
                )}
            </div>
            <div className="flex flex-col gap-3">
                {visibleInsights.map((insight) => (
                    <InsightCard
                        key={insight.key}
                        insightData={insight}
                        onAnalyticsEvent={(event) => {
                            sendAnalytics(event);
                        }}
                        onVisibleChanged={(isVisible) => {
                            setInsightVisibility(insight.insight.id, isVisible);
                        }}
                    />
                ))}
                {hiddenCount > 0 && (
                    <div className="flex justify-end">
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                                setIsExpanded(!isExpanded);
                                if (!isExpanded) {
                                    sendEvent('sales_insight_section_expanded', {
                                        customer_id: customerId,
                                        section,
                                        total_count: insights.length,
                                        initial_visible_count: INITIAL_VISIBLE_COUNT,
                                    });
                                }
                            }}
                        >
                            {isExpanded ? t('show_less') : t('show_more')}
                            <ChevronDownIcon
                                className={`ml-1 h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}
